import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Headphones, MessageSquareHeart, CalendarHeart, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contents, setContents] = useState([]);
  const [testimonies, setTestimonies] = useState([]);

  useEffect(() => {
    api.get("/contents").then(({ data }) => setContents(data.slice(0, 3))).catch(() => {});
    api.get("/testimonies").then(({ data }) => setTestimonies(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <p className="eyebrow mb-4">Your threshold</p>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-4">Hello {user?.name?.split(" ")[0]}.</h1>
      <p className="text-muted-foreground text-lg max-w-2xl leading-relaxed mb-14">
        Here it's not about being seen, but about feeling heard. Take the time you need: listen, recognise yourself and, if you wish, share your story.
      </p>

      <div className="grid md:grid-cols-3 gap-6 mb-16">
        {[
          { icon: Headphones, t: "Listen", d: "Podcasts and reflections by Maria Gioia", to: "/ascolti" },
          { icon: MessageSquareHeart, t: "Testimonies", d: "Share a part of yourself, or listen to others", to: "/testimonianze" },
          { icon: CalendarHeart, t: "Meetings", d: "Live moments, together", to: "/incontri" },
        ].map((c) => (
          <button key={c.to} onClick={() => navigate(c.to)} data-testid={`dash-card-${c.to.slice(1)}`}
            className="text-left bg-card border border-border rounded-3xl p-8 hover:bg-secondary/40 transition-colors group">
            <c.icon className="w-7 h-7 text-primary mb-5" />
            <h3 className="font-heading text-2xl mb-2">{c.t}</h3>
            <p className="text-muted-foreground text-sm mb-4">{c.d}</p>
            <span className="text-primary text-sm inline-flex items-center gap-1">Enter <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" /></span>
          </button>
        ))}
      </div>

      {contents.length > 0 && (
        <section className="mb-16">
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-6">Latest listens</h2>
          <div className="space-y-3">
            {contents.map((c) => (
              <button key={c.id} onClick={() => navigate(`/ascolti/${c.id}`)} className="w-full text-left bg-card border border-border rounded-2xl p-5 hover:bg-secondary/40 transition-colors">
                <span className="text-xs uppercase tracking-widest text-accent">{c.type}</span>
                <p className="font-heading text-xl mt-1">{c.title}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {testimonies.length > 0 && (
        <section>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-6">Voices of the community</h2>
          <div className="space-y-4">
            {testimonies.map((t) => (
              <div key={t.id} className="bg-card border border-border rounded-2xl p-6">
                {t.title && <p className="font-heading text-lg mb-1">{t.title}</p>}
                <p className="text-muted-foreground leading-relaxed line-clamp-3">{t.body}</p>
                <p className="text-xs text-accent mt-3">— {t.author_name}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
